/**
 * Blockchain Health Cache API Service
 *
 * Fetches cached blockchain health metrics from the server.
 * Data is cached server-side for 24 hours to reduce API calls.
 */

// Types
export interface GitHubStats {
  stars: number;
  forks: number;
  openIssues: number;
  lastCommitDate: string;
  recentCommits: number;
  contributors: number;
}

export interface TokenEmission {
  date: string;
  amount: string;
  type: string;
}

export type NetworkId = 'flux' | 'hedera' | 'cronos' | 'thegraph' | 'icp' | 'algorand' | 'cardano' | 'xdc' | 'vechain' | 'axelar';

export interface BlockchainHealthData {
  network_id: NetworkId;
  github_stars: number;
  github_forks: number;
  github_open_issues: number;
  github_last_commit_date: string;
  github_recent_commits: number;
  github_contributors: number;
  network_metrics: Record<string, any>;
  max_supply: number;
  circulating_supply: number;
  circulating_percent: number;
  token_emissions: TokenEmission[];
  health_status: 'growing' | 'stable' | 'declining';
  health_score: number;
  fetched_at: string;
  expires_at: string;
  cache_ttl_hours: number;
}

export interface HealthApiResponse {
  data: BlockchainHealthData | BlockchainHealthData[];
  cached?: boolean;
  refreshed?: boolean;
}

// Network-specific types
export interface FluxNetworkMetrics {
  totalApps: number;
  totalNodes: number;
  nodeUtilization: number;
  activeInstances: number;
}

export interface HederaNetworkMetrics {
  totalNodes: number;
  totalAccounts: string;
  totalTransactions: string;
  tps: number;
  totalSupply: string;
  stakingRewardRate: number;
}

export interface CronosNetworkMetrics {
  totalValidators: number;
  activeValidators: number;
  totalStaked: string;
  stakingAPY: number;
  blockHeight: string;
  avgBlockTime: number;
}

export interface TheGraphNetworkMetrics {
  totalIndexers: number;
  totalDelegators: number;
  totalCurators: number;
  totalSubgraphs: number;
  totalGRTStaked: string;
  currentEpoch: number;
}

export interface IcpNetworkMetrics {
  totalSubnets: number;
  totalNodes: number;
  totalCanisters: string;
  tps: number;
  avgBlockTime: number;
  stakingAPY: number;
}

export interface AlgorandNetworkMetrics {
  lastRound: string;
  totalAccounts: string;
  totalAssets: string;
  tps: number;
  blockTime: number;
  stakingAPY: number;
}

export interface CardanoNetworkMetrics {
  totalPools: number;
  activePools: number;
  totalStaked: string;
  stakingAPY: number;
  currentEpoch: number;
  blocksPerEpoch: number;
}

export interface XdcNetworkMetrics {
  totalMasternodes: number;
  activeMasternodes: number;
  blockHeight: string;
  tps: number;
  avgBlockTime: number;
  stakingAPY: number;
}

export interface VechainNetworkMetrics {
  totalAuthorityNodes: number;
  activeNodes: number;
  blockHeight: string;
  tps: number;
  avgBlockTime: number;
  vthoGenerationRate: number;
}

export interface AxelarNetworkMetrics {
  activeValidators: number;
  totalValidators: number;
  totalStaked: string;
  supportedChains: number;
  totalTransfers: string;
  stakingAPY: number;
}

// API Configuration
const API_BASE = '/api/monitor/health';

// In-memory cache for faster subsequent loads
const memoryCache: Map<string, { data: BlockchainHealthData; timestamp: number }> = new Map();
const MEMORY_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch health metrics for a specific network
 */
export async function fetchNetworkHealth(
  network: NetworkId
): Promise<BlockchainHealthData | null> {
  const cacheKey = `health_${network}`;

  // Check memory cache first
  const cached = memoryCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < MEMORY_CACHE_TTL) {
    console.log(`[HealthAPI] Using memory cache for ${network}`);
    return cached.data;
  }

  try {
    const response = await fetch(`${API_BASE}?network=${network}`);

    if (!response.ok) {
      console.error(`[HealthAPI] Error fetching ${network}:`, response.status);
      return null;
    }

    const result: HealthApiResponse = await response.json();
    const data = result.data as BlockchainHealthData;

    // Update memory cache
    memoryCache.set(cacheKey, { data, timestamp: Date.now() });

    return data;
  } catch (error) {
    console.error(`[HealthAPI] Error fetching ${network}:`, error);
    return null;
  }
}

/**
 * Fetch health metrics for all networks at once
 */
export async function fetchAllNetworksHealth(): Promise<Map<string, BlockchainHealthData>> {
  const result = new Map<string, BlockchainHealthData>();

  try {
    const response = await fetch(API_BASE);

    if (!response.ok) {
      console.error('[HealthAPI] Error fetching all networks:', response.status);
      return result;
    }

    const apiResponse: HealthApiResponse = await response.json();
    const dataArray = apiResponse.data as BlockchainHealthData[];

    for (const data of dataArray) {
      result.set(data.network_id, data);
      // Update memory cache
      memoryCache.set(`health_${data.network_id}`, { data, timestamp: Date.now() });
    }
  } catch (error) {
    console.error('[HealthAPI] Error fetching all networks:', error);
  }

  return result;
}

/**
 * Force refresh health data for a network
 */
export async function refreshNetworkHealth(
  network: NetworkId
): Promise<BlockchainHealthData | null> {
  try {
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'refresh', network }),
    });

    if (!response.ok) {
      console.error(`[HealthAPI] Error refreshing ${network}:`, response.status);
      return null;
    }

    const result: HealthApiResponse = await response.json();
    const data = result.data as BlockchainHealthData;

    // Update memory cache
    memoryCache.set(`health_${network}`, { data, timestamp: Date.now() });

    return data;
  } catch (error) {
    console.error(`[HealthAPI] Error refreshing ${network}:`, error);
    return null;
  }
}

/**
 * Transform cached data to component format
 */
export function transformToGitHubStats(data: BlockchainHealthData): GitHubStats {
  return {
    stars: data.github_stars ?? 0,
    forks: data.github_forks ?? 0,
    openIssues: data.github_open_issues ?? 0,
    lastCommitDate: data.github_last_commit_date ?? '',
    recentCommits: data.github_recent_commits ?? 0,
    contributors: data.github_contributors ?? 0,
  };
}

/**
 * Check if data needs refresh (client-side check)
 */
export function isDataExpired(data: BlockchainHealthData): boolean {
  if (!data.expires_at) return true;
  return new Date(data.expires_at) < new Date();
}

/**
 * Format supply number for display
 */
export function formatSupply(num: number | null | undefined): string {
  if (num == null) return '0';
  if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(0)}M`;
  return num.toLocaleString();
}

/**
 * Get time since last update
 */
export function getTimeSinceUpdate(data: BlockchainHealthData): string {
  if (!data.fetched_at) return 'N/A';

  const fetchedAt = new Date(data.fetched_at);
  const now = new Date();
  const diffMs = now.getTime() - fetchedAt.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (diffHours >= 24) {
    const days = Math.floor(diffHours / 24);
    return `Il y a ${days} jour${days > 1 ? 's' : ''}`;
  }
  if (diffHours > 0) {
    return `Il y a ${diffHours}h${diffMins > 0 ? ` ${diffMins}min` : ''}`;
  }
  return `Il y a ${diffMins} min`;
}

/**
 * Clear memory cache (useful for testing or forced refresh)
 */
export function clearMemoryCache(): void {
  memoryCache.clear();
}

/**
 * Fetch historical health data for trend analysis
 */
export interface HistoryEntry {
  network_id: NetworkId;
  snapshot_date: string;
  github_stars: number;
  github_forks: number;
  github_open_issues: number;
  github_recent_commits: number;
  circulating_supply: number;
  circulating_percent: number;
  health_status: 'growing' | 'stable' | 'declining';
  health_score: number;
  network_metrics: Record<string, any>;
  fetched_at: string;
}

export interface HistoryApiResponse {
  data: HistoryEntry[] | Record<string, HistoryEntry[]>;
  network?: NetworkId;
  days: number;
}

/**
 * Fetch historical health data for a specific network
 */
export async function fetchNetworkHistory(
  network: NetworkId,
  days: number = 30
): Promise<HistoryEntry[]> {
  try {
    const response = await fetch(`${API_BASE}?network=${network}&history=true&days=${days}`);

    if (!response.ok) {
      console.error(`[HealthAPI] Error fetching history for ${network}:`, response.status);
      return [];
    }

    const result: HistoryApiResponse = await response.json();
    return (result.data as HistoryEntry[]) || [];
  } catch (error) {
    console.error(`[HealthAPI] Error fetching history for ${network}:`, error);
    return [];
  }
}

/**
 * Fetch historical health data for all networks
 */
export async function fetchAllNetworksHistory(
  days: number = 30
): Promise<Record<string, HistoryEntry[]>> {
  try {
    const response = await fetch(`${API_BASE}?history=true&days=${days}`);

    if (!response.ok) {
      console.error('[HealthAPI] Error fetching all networks history:', response.status);
      return {};
    }

    const result: HistoryApiResponse = await response.json();
    return (result.data as Record<string, HistoryEntry[]>) || {};
  } catch (error) {
    console.error('[HealthAPI] Error fetching all networks history:', error);
    return {};
  }
}

/**
 * Calculate trend from history data
 */
export function calculateTrend(
  history: HistoryEntry[],
  metric: 'github_stars' | 'github_forks' | 'health_score' | 'circulating_supply'
): { change: number; percentChange: number; trend: 'up' | 'down' | 'stable' } {
  if (history.length < 2) {
    return { change: 0, percentChange: 0, trend: 'stable' };
  }

  const latest = history[0]?.[metric] || 0;
  const oldest = history[history.length - 1]?.[metric] || 0;

  if (oldest === 0) {
    return { change: latest, percentChange: 0, trend: latest > 0 ? 'up' : 'stable' };
  }

  const change = latest - oldest;
  const percentChange = ((change / oldest) * 100);

  let trend: 'up' | 'down' | 'stable' = 'stable';
  if (percentChange > 1) trend = 'up';
  else if (percentChange < -1) trend = 'down';

  return { change, percentChange, trend };
}
